import useIsSaved from '@gaia/hooks/useIsSaved';
import {useUser} from '@gaia/stores/user';
import type {ImportIdle, ImportSaving, ImportState, SharedData} from '@gaia/types';
import saveArea, {getIsAreaModifiedSinceSave} from '@gaia/utils/gaia/saveArea';
import {useCallback, useEffect, useState} from 'react';

import bulkArchive from './dispatchers/bulkArchive';
import create from './dispatchers/create';
import remove from './dispatchers/remove';
import takeLocal from './dispatchers/takeLocal';
import update from './dispatchers/update';
import updatePointProperties from './dispatchers/updatePointProperties';
import updateProperties from './dispatchers/updateProperties';

export type AreaDispatcher = (action: AreaAction) => string[];

export function useAreas(
    sharedData: SharedData,
    interactionType: InteractionState['type'],
    importState: ImportState
): [{[areaLocalId: string]: Area}, AreaDispatcher, number] {
    const {isAuthenticated} = useUser();

    const [areas, setAreas] = useState<{[areaLocalId: string]: Area}>({});

    const [unsaved, addSavePromise] = useIsSaved();

    const dispatcher = useCallback(
        (action: AreaAction): string[] => {
            if (action.type === 'create') {
                return create(action, setAreas);
            } else if (action.type === 'update') {
                return update(action, setAreas);
            } else if (action.type === 'takeLocal') {
                return takeLocal(action, areas, setAreas, sharedData);
            } else if (action.type === 'remove') {
                return remove(action, setAreas);
            } else if (action.type === 'updateProperties') {
                return updateProperties(action, setAreas);
            } else if (action.type === 'updatePointProperties') {
                return updatePointProperties(action, setAreas);
            } else if (action.type === 'bulkArchive') {
                return bulkArchive(action, setAreas);
            } else {
                throw new Error('Unknown action type');
            }
        },
        [areas, sharedData]
    );

    // When an area has been updated locally, save those changes back to the server.
    useEffect(() => {
        if (isSavingAllowed(interactionType, importState, isAuthenticated)) {
            for (const localId in areas) {
                const area = areas[localId];

                // If a new folder has been created while importing,
                // file all imported areas to it.
                if (
                    importState.status === 'saving' &&
                    importState.folder &&
                    area.properties.isLatestImport &&
                    !area.properties.parent_folder_id
                ) {
                    area.properties.parent_folder_id = importState.folder.id;
                }
                if (getIsAreaModifiedSinceSave(area)) {
                    addSavePromise(saveArea(area, dispatcher));
                }
            }
        }
    }, [addSavePromise, areas, dispatcher, importState, interactionType, isAuthenticated]);

    return [areas, dispatcher, unsaved];
}

export function isSavingAllowed(
    interactionType: InteractionState['type'],
    importState: ImportState,
    isAuthenticated: boolean
): importState is ImportIdle | ImportSaving {
    return (
        interactionType === 'normal' &&
        // Block automatic saving during import process
        (importState.status === 'idle' ||
            (importState.status === 'saving' && importState.folder !== undefined)) &&
        isAuthenticated
    );
}
